import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfigStore } from '@stores/configStore';
import { Settings as SettingsIcon, Save, RotateCcw, Download, Upload, AlertCircle, CheckCircle, Home } from 'lucide-react';
import {
  PlatformSelectionForm,
  DatabaseConfigForm,
  OracleDatabaseForm,
  AISAdminPanelForm,
  InvoiceProcessingTab,
  APIConfigForm,
  EBSConfigForm,
  SecurityConfigForm,
  LoggingConfigForm,
  PlatformSpecificConfigForm,
  ConnectionTestingPanel,
  ConfigurationTemplatesPanel,
} from '@components/index';

type TabType = 'platform' | 'database' | 'ais' | 'invoiceProcessing' | 'api' | 'ebs' | 'security' | 'logging' | 'platformSpecific' | 'connectionTesting' | 'templates';

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const {
    config,
    isLoading,
    error,
    validationResult,
    loadConfig,
    saveConfig,
    validateConfig,
    resetToDefaults,
    exportConfig,
    importConfig,
    testDatabaseConnection,
    testAPIConnection,
    clearError,
  } = useConfigStore();

  const [activeTab, setActiveTab] = useState<TabType>('platform');
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({});
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      validateConfig();
      await saveConfig();
      alert('Configuration saved successfully');
    } catch (err) {
      alert('Failed to save configuration');
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset to default configuration?')) {
      try {
        await resetToDefaults();
        alert('Configuration reset to defaults');
      } catch (err) {
        alert('Failed to reset configuration');
      }
    }
  };

  const handleExport = () => {
    const json = exportConfig();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oracle-adapter-config-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const json = e.target?.result as string;
        await importConfig(json);
        alert('Configuration imported successfully');
      } catch (err) {
        alert('Failed to import configuration');
      }
    };
    reader.readAsText(file);
  };

  const handleTestDatabase = async () => {
    const result = await testDatabaseConnection();
    setTestResults((prev) => ({ ...prev, database: result }));
  };

  const handleTestAPI = async () => {
    const result = await testAPIConnection();
    setTestResults((prev) => ({ ...prev, api: result }));
  };

  // Get platform-specific tab label
  const getPlatformTabLabel = () => {
    const platform = config.platform?.selectedPlatform;
    if (platform === 'EBS') return 'EBS Configuration';
    if (platform === 'FUSION') return 'Oracle Fusion Configuration';
    if (platform === 'NETSUITE') return 'Oracle NetSuite Configuration';
    if (platform === 'JDE') return 'JD Edwards Configuration';
    if (platform === 'PSFT') return 'Oracle PeopleSoft Configuration';
    return 'Platform Configuration';
  };

  const tabs: { id: TabType; label: string }[] = [
    { id: 'platform', label: 'Platform Selection' },
    { id: 'database', label: 'Database' },
    { id: 'ais', label: 'AIS Admin Panel' },
    { id: 'invoiceProcessing', label: 'Invoice Processing' },
    { id: 'ebs', label: getPlatformTabLabel() },
    { id: 'api', label: 'API Configuration' },
    { id: 'security', label: 'Security' },
    { id: 'logging', label: 'Logging & Monitoring' },
    { id: 'platformSpecific', label: 'Platform-Specific' },
    { id: 'connectionTesting', label: 'Connection Testing' },
    { id: 'templates', label: 'Configuration Templates' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <SettingsIcon className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">System Configuration</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              title="Go to Dashboard"
            >
              <Home className="w-4 h-4" />
              Home
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
              <Upload className="w-4 h-4" />
              Import
              <input type="file" accept=".json" onChange={handleImport} className="hidden" />
            </label>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800">{error}</p>
            </div>
            <button onClick={clearError} className="text-red-600 hover:text-red-800">
              ×
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200 overflow-x-auto">
          <div className="flex gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          {activeTab === 'platform' && <PlatformSelectionForm />}

          {activeTab === 'database' && (
            <DatabaseConfigForm onTestConnection={handleTestDatabase} testResult={testResults.database} />
          )}

          {activeTab === 'ais' && (
            <AISAdminPanelForm onTestConnection={handleTestAPI} testResult={testResults.api} />
          )}

          {activeTab === 'invoiceProcessing' && <InvoiceProcessingTab />}

          {activeTab === 'ebs' && (
            <EBSConfigForm 
              platform={config.platform?.selectedPlatform || 'EBS'}
              onTestConnection={handleTestDatabase} 
              testResult={testResults.ebs} 
            />
          )}

          {activeTab === 'api' && (
            <div className="space-y-4">
              <APIConfigForm />
            </div>
          )}

          {activeTab === 'security' && (
            <SecurityConfigForm />
          )}

          {activeTab === 'logging' && (
            <LoggingConfigForm />
          )}

          {activeTab === 'platformSpecific' && (
            <PlatformSpecificConfigForm platform={config.platform?.selectedPlatform || 'EBS'} />
          )}

          {activeTab === 'connectionTesting' && (
            <ConnectionTestingPanel />
          )}

          {activeTab === 'templates' && (
            <ConfigurationTemplatesPanel />
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isLoading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};
